import { useCallback, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { useLocation } from 'react-router-dom';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/transitional/components/View';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { SCREENS } from '../../../constants/Tracking';
import { MY_PLAYLISTS_URL_FRAGMENT } from '../../../constants/Navigation';
import useNavigate from '../../../hooks/useNavigate';
import { useCreatePlaylist } from '../../savedItems/savedItemsQueries';
import useWootricSurvey from '../../../hooks/useWootricSurvey';
import PaginatedUICardGrid from '../../../containers/MyLearningPage/PaginatedUICardGrid';
import { MY_SAVED_ITEMS_PAGE_SIZE } from '../../savedItems';
import ApiError from '../../../components/Errors/ApiError';
import NoResultsError from '../../../components/Errors/NoResultsError';
import { useMyLearningParams } from '../myLearningHooks';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import BlankPageMediaWrapper from '../../../components/BlankPageMediaWrapper';
import { LearningContentCard } from '../../cards';
import { useMyLearningCardModels } from '../myLearningQueries';
import { PAGE_SIZE, usePagination } from '../../pagination';
import { MY_LEARNING_FILTERS, ContentTypesFilterToMyLearningRequestMapping, MyLearningTabTypeToRequestMapping, ProgressTypeFilterToMyLearningRequestMapping } from '../myLearningConsts';
import { FiltersCard } from '../../filters/components/FiltersCard';
import { PageHeader } from '../../header/components/PageHeader';
import { CertificationsFilter } from '../../filters/components/CertificationsFilter';
import { ProgressFilter } from '../../filters/components/ProgressFilter';
import { AllContentTypesFilter } from '../../filters/components/AllContentTypesFilter';
import { PageResultsCount, PageResultsCountPlaceholder } from '../../header/components/PageResultsCount';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { SearchFilter } from '../../filters/components/SearchFilter';
import I18n from 'I18n';
import { MobileFilterButton } from '../../filters/components/MobileFilterButton';
import { scrollPageToTop } from 'academy-common-lib/utils/scrolling';
import { ContentSourceFilter } from '../../filters/components/ContentSourceFilter';
import { HasGate } from '../../accounts/components/HasGate';
import { useDisplayedData } from '../../table/tableHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useMyLearningTab = () => {
  const myLearningParams = useMyLearningParams();
  const {
    tab,
    page,
    contentTypes,
    hasCertification,
    isAnyFilterSelected,
    progressType,
    searchTerm,
    visibilityGroup
  } = myLearningParams;
  const {
    myLearningCardModels,
    myLearningCardModelsLoading,
    myLearningCardModelsError,
    totalCount
  } = useMyLearningCardModels({
    contentTypes: contentTypes !== null && contentTypes !== void 0 && contentTypes.length ? contentTypes.map(t => ContentTypesFilterToMyLearningRequestMapping[t]).flat() : MyLearningTabTypeToRequestMapping[tab],
    offset: {
      cursor: (page - 1) * PAGE_SIZE
    },
    pageLength: PAGE_SIZE,
    progressType: progressType ? ProgressTypeFilterToMyLearningRequestMapping[progressType] : undefined,
    searchTerm,
    hasCertification,
    includePlaylistProgress: true,
    visibilityGroup: visibilityGroup === 'ALL_CONTENT' ? undefined : visibilityGroup
  });
  const {
    cardModels: displayedCardModels,
    totalCount: displayedTotalCount = 0
  } = useDisplayedData({
    cardModels: myLearningCardModels,
    totalCount
  }, {
    loading: myLearningCardModelsLoading,
    parameters: myLearningParams,
    initialOnly: true
  }) || {};
  return {
    myLearningResults: myLearningCardModels,
    displayedResults: displayedCardModels,
    myLearningLoading: myLearningCardModelsLoading,
    myLearningError: myLearningCardModelsError,
    totalCount: displayedTotalCount,
    tab,
    page,
    isAnyFilterSelected,
    searchTerm
  };
};
const ItemRightComponent = props => {
  return /*#__PURE__*/_jsx(Illustration, Object.assign({
    inlineSize: "100%",
    name: "training-academy"
  }, props));
};
const MyLearningNoResults = () => {
  const {
    tab
  } = useMyLearningParams();
  const {
    isMobile
  } = useMediaQuery();
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MYLEARNING_LISTING_NO_RESULTS",
    children: /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      marginBlockStart: isMobile ? '600' : '600',
      children: /*#__PURE__*/_jsxs(BlankPageMediaWrapper, {
        "data-test-id": "my-learning-empty-state",
        itemRightComponent: ItemRightComponent,
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.myLearning.emptyState.${tab}.title`
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.myLearning.emptyState.${tab}.bodyOne`
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.myLearning.emptyState.${tab}.bodyTwo`
          })
        })]
      })
    })
  });
};
const MyLearningNoResultsWithFilter = () => {
  const location = useLocation();
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MYLEARNING_LISTING_NO_RESULTS",
    children: /*#__PURE__*/_jsx(NoResultsError, {
      resetLink: location.pathname
    })
  });
};
const MyLearningResults = ({
  totalCount,
  results,
  displayedCardModels
}) => {
  const {
    page
  } = useMyLearningParams();
  const {
    isTablet
  } = useMediaQuery();
  const {
    setPage
  } = usePagination();
  const isGhost = useCallback(item => !!results && !results.some(c => c.id === item.id && c.type === item.type), [results]);
  const handlePageChange = pageNumber => {
    setPage(pageNumber);
    scrollPageToTop();
  };
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MYLEARNING_LISTING_LOADED",
    children: /*#__PURE__*/_jsx(PaginatedUICardGrid, {
      isLoadingPageOnDemand: true,
      limit: MY_SAVED_ITEMS_PAGE_SIZE,
      onPageChange: handlePageChange,
      page: page,
      shouldRenderOneColumn: isTablet,
      totalCount: totalCount,
      children: displayedCardModels.map(item => /*#__PURE__*/_jsx(LearningContentCard, {
        isGhost: isGhost(item),
        item: item,
        overviewType: "my-learning",
        size: isTablet ? 'small' : 'large'
      }, `item_${item.id}`))
    })
  });
};
const MyLearningContent = ({
  myLearningError,
  myLearningLoading,
  myLearningResults,
  displayedResults,
  totalCount,
  isAnyFilterSelected
}) => {
  return myLearningError ? /*#__PURE__*/_jsx(ApiError, {
    error: myLearningError,
    marker404: "MYLEARNING_LISTING_API_404",
    markerDefault: "MYLEARNING_LISTING_API_ERROR"
  }) : myLearningLoading && !displayedResults ? /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    minHeight: 100
  }) : displayedResults !== null && displayedResults !== void 0 && displayedResults.length ? /*#__PURE__*/_jsx(MyLearningResults, {
    displayedCardModels: displayedResults,
    results: myLearningResults,
    totalCount: totalCount
  }) : isAnyFilterSelected ? /*#__PURE__*/_jsx(MyLearningNoResultsWithFilter, {}) : /*#__PURE__*/_jsx(MyLearningNoResults, {});
};
export const MyLearningTabs = () => {
  useWootricSurvey('CSAT');
  const {
    isMobile
  } = useMediaQuery();
  const {
    myLearningResults,
    displayedResults,
    myLearningLoading,
    myLearningError,
    totalCount,
    page,
    isAnyFilterSelected
  } = useMyLearningTab();
  const navigate = useNavigate();
  const {
    createPlaylist,
    createPlaylistLoading
  } = useCreatePlaylist({
    onCompleted(playlist) {
      UsageTracker.trackStandalone('Playlist Interaction', {
        action: 'playlist-created',
        location: 'my-learning-page-header'
      });
      navigate(`${MY_PLAYLISTS_URL_FRAGMENT}/${playlist.id}`);
    },
    onError() {
      FloatingAlertStore.addAlert({
        message: I18n.text('learningCenter.savedItems.playlistDetails.createPlaylistError'),
        type: 'danger'
      });
    }
  });
  const onCreatePlaylistClick = () => {
    UsageTracker.trackStandalone('Playlist Interaction', {
      action: 'create-playlist',
      location: 'my-learning-page-header'
    });
    createPlaylist({
      name: I18n.text('learningCenter.savedItems.newPlaylistTitle'),
      description: '',
      items: []
    }).catch(error => {
      Raven.captureException(error);
    });
  };
  useEffect(() => {
    UsageTracker.trackStandalone('Pageview', {
      screen: SCREENS.MY_LEARNING
    });
  }, []);
  const showFiltersCard = (myLearningResults === null || myLearningResults === void 0 ? void 0 : myLearningResults.length) || isAnyFilterSelected;
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "my-learning-page",
    children: [/*#__PURE__*/_jsx(PageHeader, {
      subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.heading.headingSubTitles.MY_LEARNING"
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.heading.headingTitles.MY_LEARNING"
      }),
      children: !isMobile && /*#__PURE__*/_jsx(LoadingButton, {
        loading: createPlaylistLoading,
        onClick: onCreatePlaylistClick,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.createPlaylist"
        })
      })
    }), showFiltersCard && /*#__PURE__*/_jsx(HasGate, {
      gate: "Academy:TrainerContent",
      children: /*#__PURE__*/_jsx(ContentSourceFilter, {
        appearsIn: "my-learning"
      })
    }), !isMobile && /*#__PURE__*/_jsxs(_Fragment, {
      children: [!myLearningLoading ? /*#__PURE__*/_jsx(PageResultsCount, {
        page: page - 1,
        pageSize: PAGE_SIZE,
        total: totalCount
      }) : /*#__PURE__*/_jsx(PageResultsCountPlaceholder, {}), showFiltersCard && /*#__PURE__*/_jsxs(FiltersCard, {
        children: [/*#__PURE__*/_jsx(SearchFilter, {
          appearsIn: "my-learning",
          marginInlineEnd: "600",
          maxInlineSize: "300px",
          placeholder: I18n.text('learningCenter.myLearning.searchPlaceholder')
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.myLearning.filterBy"
        }), /*#__PURE__*/_jsx(CertificationsFilter, {
          appearsIn: "my-learning"
        }), /*#__PURE__*/_jsx(ProgressFilter, {
          appearsIn: "my-learning"
        }), /*#__PURE__*/_jsx(AllContentTypesFilter, {
          appearsIn: "my-learning"
        })]
      })]
    }), isMobile && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(TFlex, {
        alignItems: "center",
        gap: 16,
        marginBlockEnd: "600",
        children: [/*#__PURE__*/_jsx(SearchFilter, {
          appearsIn: "my-learning",
          placeholder: I18n.text('learningCenter.myLearning.searchPlaceholder')
        }), /*#__PURE__*/_jsx(MobileFilterButton, {
          mobileFilterTypes: MY_LEARNING_FILTERS['all'],
          totalCount: totalCount
        })]
      }), !myLearningLoading && /*#__PURE__*/_jsx(PageResultsCount, {
        page: page - 1,
        pageSize: PAGE_SIZE,
        total: totalCount
      })]
    }), /*#__PURE__*/_jsx(View, {
      children: /*#__PURE__*/_jsx(MyLearningContent, {
        displayedResults: displayedResults || null,
        isAnyFilterSelected: isAnyFilterSelected,
        myLearningError: myLearningError,
        myLearningLoading: myLearningLoading,
        myLearningResults: myLearningResults,
        totalCount: totalCount
      })
    })]
  });
};